/**
 * Pure menu-shaping helpers shared by menuDataBuilder and the template engine.
 *
 * Kept free of React and of the "use client" barrel (util.ts) so server code —
 * the native-client menu experience route — can build MenuData exactly the way
 * the browser does. util.ts re-exports everything here, so existing imports
 * are unchanged.
 */

import type { Category, MenuSection } from "../types/template";
import { generateSlug } from "@/lib/utils";
import { normalizeLookupKey } from "./templateNodes";


export const DEFAULT_TIMEZONE = "UTC";

export function getZonedDateParts(date: Date, timezone?: string) {
  const tz = timezone || DEFAULT_TIMEZONE;
  const formatter = new Intl.DateTimeFormat("en-US", {
    timeZone: tz,
    year: "numeric",
    month: "numeric",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  });

  const parts = formatter.formatToParts(date);
  const map = Object.fromEntries(parts.map((part) => [part.type, part.value]));

  const month = Number(map.month);
  const day = Number(map.day);
  const year = Number(map.year);
  const hour = Number(map.hour);
  const minute = Number(map.minute);

  return {
    year,
    month,
    day,
    hour,
    minute,
    minutesOfDay: hour * 60 + minute,
    dateKey: `${year}-${String(month).padStart(2, "0")}-${String(day).padStart(2, "0")}`,
    weekday: date.toLocaleDateString("en-US", { weekday: "long", timeZone: tz }).toLowerCase(),
  };
}

export function parseTimeToMinutes(value?: string | null): number | null {
  if (!value) return null;
  const [h, m] = value.split(":").map((entry) => Number(entry));
  if (!Number.isFinite(h) || !Number.isFinite(m)) return null;
  return h * 60 + m;
}

export function isItemScheduleActive(item: unknown, timezone?: string) {
  const it = item as Record<string, unknown> | null | undefined;
  const hasDateRange = Boolean(it?.available_from || it?.available_until);
  const hasDays = Array.isArray(it?.available_Days) && (it.available_Days as unknown[]).length > 0;
  const availableTimes = it?.available_Times as Record<string, unknown> | undefined;
  const hasTimes = Boolean(availableTimes?.start || availableTimes?.end);

  if (!hasDateRange && !hasDays && !hasTimes) {
    return true;
  }

  const now = getZonedDateParts(new Date(), timezone);

  if (it?.available_from && now.dateKey < String(it.available_from)) {
    return false;
  }
  if (it?.available_until && now.dateKey > String(it.available_until)) {
    return false;
  }

  if (hasDays) {
    const allowedDays = (it.available_Days as string[]).map((day: string) => String(day).toLowerCase());
    if (!allowedDays.includes(now.weekday)) {
      return false;
    }
  }

  if (hasTimes) {
    const start = parseTimeToMinutes((it?.available_Times as { start?: string })?.start);
    const end = parseTimeToMinutes((it?.available_Times as { end?: string })?.end);
    const current = now.minutesOfDay;

    if (start !== null && end !== null) {
      if (start <= end) {
        if (current < start || current > end) return false;
      } else {
        if (current < start && current > end) return false;
      }
    } else if (start !== null && current < start) {
      return false;
    } else if (end !== null && current > end) {
      return false;
    }
  }

  return true;
}

export function pickCategoryCollectionId(category: Record<string, unknown> | null | undefined, fallbackId: string): string {
  return (
    String(category?.collection_id ?? '') ||
    String(category?.collectionId ?? '') ||
    String((category?.collection as Record<string, unknown>)?.id ?? '') ||
    String((category?.metadata as Record<string, unknown>)?.collection_id ?? '') ||
    String((category?.metadata as Record<string, unknown>)?.collectionId ?? '') ||
    fallbackId
  );
}

export function readStr(val: unknown): string | undefined {
  return typeof val === 'string' ? val : undefined;
}

export function readNum(val: unknown): number | undefined {
  return typeof val === 'number' ? val : undefined;
}

export function readBool(val: unknown): boolean | undefined {
  return typeof val === 'boolean' ? val : undefined;
}

export function slugifyMenuCollection(value: unknown): string {
  return generateSlug(String(value || 'menu'), 'menu');
}

export function buildSectionArtifacts(categories: Category[]): {
  sections: MenuSection[];
  categoriesBySection: Record<string, Category[]>;
} {
  const sectionsMap = new Map<
    string,
    {
      id: string;
      name: string;
      description?: string;
      order: number;
      categories: Category[];
      featuredItems: unknown[];
    }
  >();

  categories.forEach((category, idx) => {
    const rawSectionId = category.section_id || 'default';
    const sectionId = String(rawSectionId);
    const sectionKey = normalizeLookupKey(sectionId) || 'default';
    const sectionName =
      category.section_label ||
      (sectionId === 'default' ? 'Menu' : sectionId.replace(/[_-]+/g, ' '));
    const sectionOrder = Number(category.section_order ?? idx);

    if (!sectionsMap.has(sectionKey)) {
      sectionsMap.set(sectionKey, {
        id: sectionId,
        name: sectionName,
        description: category.description,
        order: Number.isFinite(sectionOrder) ? sectionOrder : idx,
        categories: [],
        featuredItems: [],
      });
    }

    const sectionEntry = sectionsMap.get(sectionKey)!;
    sectionEntry.categories.push(category);
    sectionEntry.featuredItems.push(...(category.featured_items || []));
  });

  const sections = Array.from(sectionsMap.values())
    .sort((a, b) => a.order - b.order)
    .map((entry) => ({
      id: entry.id,
      name: entry.name,
      description: entry.categories.find((category) => category.description)?.description,
      categories: entry.categories,
      featuredItems: entry.featuredItems,
    })) as MenuSection[];

  const categoriesBySection: Record<string, Category[]> = {};
  sections.forEach((section) => {
    categoriesBySection[section.id] = section.categories;
    categoriesBySection[normalizeLookupKey(section.id)] = section.categories;
  });

  return { sections, categoriesBySection };
}
